import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { authService } from "../services/authService";
import { User, LoginCredentials, RegisterData } from "../types/auth";
import toast from "react-hot-toast";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: "AUTH_START" }
  | { type: "AUTH_SUCCESS"; payload: User }
  | { type: "AUTH_ERROR"; payload: string }
  | { type: "AUTH_LOGOUT" }
  | { type: "CLEAR_ERROR" }
  | { type: "UPDATE_USER"; payload: Partial<User> };

interface AuthContextType extends AuthState {
  isAdmin: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  clearError: () => void;
}

const initialState: AuthState = {
  user: null,
  loading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "AUTH_START":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "AUTH_SUCCESS":
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
      };
    case "AUTH_ERROR":
      return {
        ...state,
        user: null,
        loading: false,
        error: action.payload,
      };
    case "AUTH_LOGOUT":
      return {
        ...state,
        user: null,
        loading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Helper function to handle token refresh
  const handleTokenRefresh = useCallback(async (): Promise<void> => {
    try {
      const refreshTokenValue = localStorage.getItem("refreshToken");
      if (!refreshTokenValue) {
        throw new Error("No refresh token available");
      }

      const response = await authService.refreshToken(refreshTokenValue);

      // Update tokens
      localStorage.setItem("accessToken", response.accessToken);
      localStorage.setItem("refreshToken", response.refreshToken);

      // Ensure auth state always resolves after refresh
      if (response.user) {
        dispatch({ type: "AUTH_SUCCESS", payload: response.user });
      } else {
        const currentUser = await authService.getCurrentUser();
        dispatch({ type: "AUTH_SUCCESS", payload: currentUser });
      }
    } catch (error: any) {
      // Refresh failed, logout
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      dispatch({ type: "AUTH_LOGOUT" });
      throw error;
    }
  }, []);

  // Helper function to handle logout
  const handleLogout = useCallback((): void => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        authService.logout().catch(() => {
          // Ignore logout errors
        });
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      dispatch({ type: "AUTH_LOGOUT" });
      toast.success("Logged out successfully");
    }
  }, []);

  // Initialize auth state on app load
  useEffect(() => {
    let didTimeout = false;
    let didResolve = false;

    const resolveAuth = (action: AuthAction) => {
      if (!didResolve && !didTimeout) {
        didResolve = true;
        dispatch(action);
      }
    };

    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (token) {
          // Verify token and get user data
          const user = await authService.getCurrentUser();
          resolveAuth({ type: "AUTH_SUCCESS", payload: user });
        } else {
          resolveAuth({ type: "AUTH_LOGOUT" });
        }
      } catch (error) {
        // Token might be expired, try to refresh
        try {
          await handleTokenRefresh();
          didResolve = true;
        } catch (refreshError) {
          // Refresh failed — ensure we always clear loading state
          resolveAuth({ type: "AUTH_LOGOUT" });
        }
      } finally {
        // Final fail-safe: never leave auth in loading state indefinitely
        if (!didResolve && !didTimeout) {
          resolveAuth({ type: "AUTH_LOGOUT" });
        }
      }
    };

    // Safety timeout: if auth initialization takes >10s, force logout
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      didResolve = true;
      console.warn("Auth initialization timed out, forcing logout");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      dispatch({ type: "AUTH_LOGOUT" });
    }, 10000);

    initializeAuth().finally(() => {
      clearTimeout(timeoutId);
    });

    return () => {
      clearTimeout(timeoutId);
    };
  }, [handleTokenRefresh]);

  // Listen for token refresh events from API interceptor
  useEffect(() => {
    const handleTokenRefreshEvent = (event: CustomEvent) => {
      const { user } = event.detail;
      if (user) {
        dispatch({ type: "AUTH_SUCCESS", payload: user });
      }
    };

    window.addEventListener(
      "tokenRefreshed",
      handleTokenRefreshEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "tokenRefreshed",
        handleTokenRefreshEvent as EventListener
      );
    };
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (state.user) {
      // Refresh token every 14 minutes (tokens expire in 15 minutes)
      const interval = setInterval(
        () => {
          handleTokenRefresh().catch(() => {
            // If refresh fails, logout is already handled in handleTokenRefresh
          });
        },
        14 * 60 * 1000
      );

      return () => clearInterval(interval);
    }
  }, [state.user, handleTokenRefresh]);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    try {
      dispatch({ type: "AUTH_START" });
      const response = await authService.login(credentials);

      // Store tokens
      localStorage.setItem("accessToken", response.accessToken);
      localStorage.setItem("refreshToken", response.refreshToken);

      dispatch({ type: "AUTH_SUCCESS", payload: response.user });
      toast.success("Welcome back!");
      return response.user;
    } catch (error: any) {
      const message = error.response?.data?.error?.message || "Login failed";
      dispatch({ type: "AUTH_ERROR", payload: message });
      toast.error(message);
      throw error;
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    try {
      dispatch({ type: "AUTH_START" });
      await authService.register(data);

      toast.success("Registration successful!");
      dispatch({ type: "AUTH_LOGOUT" });
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message || "Registration failed";
      dispatch({ type: "AUTH_ERROR", payload: message });
      toast.error(message);
      throw error;
    }
  };

  const logout = (): void => {
    handleLogout();
  };

  const refreshToken = async (): Promise<void> => {
    await handleTokenRefresh();
  };

  const updateProfile = async (data: Partial<User>): Promise<void> => {
    try {
      const updatedUser = await authService.updateProfile(data);
      dispatch({ type: "UPDATE_USER", payload: updatedUser });
      toast.success("Profile updated successfully");
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message || "Failed to update profile";
      toast.error(message);
      throw error;
    }
  };

  const clearError = (): void => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  const isAdmin =
    (state.user?.role === 'admin' || state.user?.role === 'owner') ||
    (state.user?.organization?.role === 'admin' || state.user?.organization?.role === 'owner') ||
    false;

  const value: AuthContextType = {
    ...state,
    isAdmin,
    login,
    register,
    logout,
    refreshToken,
    updateProfile,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
